import { useState, useCallback, useEffect } from 'react';

import { FileDropZone } from 'features/common-elements/FileDropZone';
import { useS3Uploader } from 'features/common-elements/useS3Uploader';
import { Button } from 'ui-kit';
import { ProgressIndicator } from 'ui-kit';

import { FileType } from '__generated__/globalTypes';

import { InProgressFile } from '../DocumentDetails';

// Map MIME types to file extensions for fallback validation
const MIME_TO_EXTENSIONS: Record<string, string[]> = {
  'application/zip': ['.zip'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ['.xlsb'],
  'text/csv': ['.csv'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tif', '.tiff'],
};

// Validate file by extension when MIME type detection fails
const isValidFileByExtension = (fileName: string, allowedMimeTypes: string[]): boolean => {
  const fileExt = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  return allowedMimeTypes.some((mimeType) =>
    MIME_TO_EXTENSIONS[mimeType]?.some((ext) => fileExt === ext),
  );
};

interface PassedProps {
  parentId: string;
  fileType: FileType;
  setIsOpen?: (isOpen: boolean) => void;
  allowedFileTypes?: string[];
  disabled?: boolean;
  showButtons?: boolean;
  width?: string;
  height?: string;
  onFileSuccess?: (file?: File, uuid?: string) => void;
  onFileDropSuccess?: (file: File, uploadFile: (file: File) => Promise<string | undefined>) => void;
  // onFileDropHandler?: (file: File) => Promise<any>;
  timeToHideProgress?: number;
  isContract?: boolean;
  isCollateral?: boolean;
  isWire?: boolean;
  vertical?: boolean;
  allowedTypesMessage?: string;
  onProgressCallback?: (progress: number, file?: File) => void;
  onUploadCallback?: (isUploading: boolean) => void;
  showProgress?: boolean;
  uploadedBy?: string;
}

const FileUpload = ({
  parentId,
  fileType,
  setIsOpen,
  allowedFileTypes = [],
  disabled = false,
  showButtons = true,
  width,
  height,
  onFileSuccess,
  onFileDropSuccess,
  timeToHideProgress,
  isContract = false,
  isCollateral = false,
  vertical = false,
  isWire = false,
  allowedTypesMessage,
  onProgressCallback,
  onUploadCallback,
  showProgress = true,
  uploadedBy,
  // onFileDropHandler,
}: PassedProps) => {
  const [uploadsInProgress, setUploadsInProgress] = useState<InProgressFile[]>([]);
  const [uploaded, setUploaded] = useState<boolean>(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (uploaded && timeToHideProgress) {
      timer = setTimeout(() => {
        setUploaded(false);

        if (onUploadCallback) {
          onUploadCallback(false);
        }

        setUploadsInProgress([]);
      }, timeToHideProgress * 1000);
    }
    return () => clearTimeout(timer);
  }, [uploaded, timeToHideProgress, onUploadCallback]);

  const onSuccess = useCallback(
    (file: File, _versionId?: string, uuid?: string) => {
      setUploadsInProgress((prev) => {
        const existingFile = prev.find(
          (f) => f.name === file.name && f.lastModified === file.lastModified,
        );
        if (existingFile) {
          existingFile.id = uuid;
        }
        return [...prev];
      });

      setUploaded(true);

      if (onUploadCallback) {
        onUploadCallback(true);
      }

      if (onFileSuccess) {
        onFileSuccess(file, uuid);
      }
    },
    [setUploadsInProgress, onFileSuccess, onUploadCallback],
  );

  const onProgress = useCallback(
    (progress: number, file?: File) => {
      if (!file) {
        return;
      }

      setUploaded(false);

      if (onUploadCallback) {
        onUploadCallback(false);
      }

      setUploadsInProgress((prev) => {
        const existingFile = prev.find(
          (f) => f.name === file.name && f.lastModified === file.lastModified,
        );
        if (existingFile) {
          existingFile.progress = {
            percentage: progress,
          };
        } else {
          prev.push({
            name: file.name,
            lastModified: file.lastModified,
            progress: {
              percentage: progress,
            },
          });
        }
        return [...prev];
      });

      if (onProgressCallback) {
        onProgressCallback(progress, file);
      }
    },
    [setUploadsInProgress, onProgressCallback, onUploadCallback],
  );

  const { upload } = useS3Uploader({
    onSuccess,
    onProgress,
    fileType,
    parentId: parentId,
    isContract,
    isCollateral,
    uploadedBy,
    isWire,
  });

  const onFileDrop = async (file: File) => {
    if (disabled) return false;

    // Validate file type: check MIME type first, fallback to extension
    if (allowedFileTypes.length > 0) {
      const hasValidMimeType = allowedFileTypes.includes(file.type);
      const hasValidExtension = isValidFileByExtension(file.name, allowedFileTypes);

      if (!hasValidMimeType && !hasValidExtension) {
        console.error(
          `File ${file.name} rejected: invalid type. Expected one of: ${allowedFileTypes.join(', ')}`,
        );
        return;
      }
    }

    if (onFileDropSuccess) {
      onFileDropSuccess(file, upload);
    } else {
      try {
        // const uploadFunc = onFileDropHandler ? onFileDropHandler : upload;
        // await uploadFunc(file);
        await upload(file);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  const handleClose = useCallback(() => {
    if (setIsOpen) {
      setIsOpen(false);
    }
  }, [setIsOpen]);

  return (
    <div className={`file_upload transition-opacity ${disabled ? 'opacity-50' : 'opacity-100'}`}>
      <FileDropZone
        onFileDrop={onFileDrop}
        allowedFileTypes={allowedFileTypes}
        width={width}
        height={height}
        disabled={disabled}
        vertical={vertical}
        allowedTypesMessage={allowedTypesMessage}
      />
      {showProgress && uploadsInProgress.length > 0 && (
        <div className="file_upload__details_container">
          {uploadsInProgress.map((file: InProgressFile, index) => (
            <div key={file.id || index} className="file_upload__progress_bar">
              <span className="file_upload__progress_bar_file_name">{file.name}</span>
              {file.progress != null && file.progress.percentage > 0 && (
                <ProgressIndicator percent={file.progress.percentage} />
              )}
            </div>
          ))}
        </div>
      )}
      {uploaded && (
        <p className="my-2 flex justify-center text-xs text-gray-100">
          Files uploaded successfully
        </p>
      )}
      {showButtons && (
        <div className="mt-4 flex justify-center gap-4">
          {uploaded && (
            <Button label="Done" size="medium" type="primary" onClick={handleClose}>
              Done
            </Button>
          )}
          <Button label="Close" size="medium" type="secondary" onClick={handleClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
